import React from 'react';
import Icon from '../../../components/AppIcon';

const RecruitmentFunnel = ({ funnelData }) => {
  const stages = [
    { id: 'applied', label: 'Applied', icon: 'FileText', color: 'bg-blue-500' },
    { id: 'screening', label: 'Screening', icon: 'Search', color: 'bg-yellow-500' },
    { id: 'internal_interview', label: 'Internal Int.', icon: 'UserCheck', color: 'bg-purple-500' },
    { id: 'external_interview', label: 'External Int.', icon: 'Users', color: 'bg-indigo-500' },
    { id: 'offer', label: 'Offer', icon: 'Mail', color: 'bg-green-500' },
    { id: 'hired', label: 'Hired', icon: 'CheckCircle', color: 'bg-emerald-600' }
  ];

  const getTotalApplicants = () => {
    return Object.values(funnelData).reduce((sum, count) => sum + count, 0);
  };

  const getConversionRate = (currentStage, previousStage) => {
    if (!previousStage) return 100;
    const current = funnelData[currentStage] || 0;
    const previous = funnelData[previousStage] || 0;
    return previous > 0 ? Math.round((current / previous) * 100) : 0;
  };

  const totalApplicants = getTotalApplicants();

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Recruitment Funnel</h3>
          <p className="text-sm text-muted-foreground">Track applicant progression through stages</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-foreground">{totalApplicants}</p>
          <p className="text-sm text-muted-foreground">Total Applicants</p>
        </div>
      </div>

      {/* Horizontal Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {stages.map((stage, index) => {
          const count = funnelData[stage.id] || 0;
          const percentage = totalApplicants > 0 ? Math.round((count / totalApplicants) * 100) : 0;
          const conversionRate = getConversionRate(stage.id, stages[index - 1]?.id);

          return (
            <div key={stage.id} className="relative">
              <div className="bg-muted/30 rounded-lg p-4 border border-border hover:border-primary/50 transition-colors">
                {/* Stage Icon and Label */}
                <div className="flex flex-col items-center text-center mb-3">
                  <div className={`${stage.color} rounded-full p-3 flex items-center justify-center mb-2`}>
                    <Icon name={stage.icon} size={20} color="white" />
                  </div>
                  <h4 className="font-medium text-foreground text-sm mb-1">{stage.label}</h4>
                </div>

                {/* Count and Percentage */}
                <div className="text-center mb-3">
                  <p className="text-2xl font-bold text-foreground">{count}</p>
                  <p className="text-xs text-muted-foreground">{percentage}% of total</p>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-muted rounded-full h-2 mb-2">
                  <div
                    className={`${stage.color} h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>

                {/* Conversion Rate */}
                {index > 0 && (
                  <div className="text-center pt-2 border-t border-border">
                    <p className="text-xs font-medium text-foreground">{conversionRate}%</p>
                    <p className="text-xs text-muted-foreground">conversion</p>
                  </div>
                )}
              </div>

              {/* Arrow between stages */}
              {index < stages.length - 1 && (
                <div className="hidden lg:block absolute -right-2 top-1/2 transform -translate-y-1/2 z-10">
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-lg font-semibold text-success">
              {Math.round(((funnelData.hired || 0) / (funnelData.applied || 1)) * 100)}%
            </p>
            <p className="text-sm text-muted-foreground">Overall Success Rate</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-primary">
              {Math.round(((funnelData.internal_interview || 0) / (funnelData.screening || 1)) * 100)}%
            </p>
            <p className="text-sm text-muted-foreground">Interview Rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentFunnel;